// Persistence helpers for the Vendors list filters (2026-08-06 design,
// Task 2, "The setters own storage; the effect only restores"). The URL stays
// the single source of truth; localStorage mirrors the last list state so the
// sidebar "Vendors" link (a bare /vendors navigation that does NOT remount
// the page) can restore it. "URL has no filter params" is the same state
// whether the admin arrived via that link or just removed their last filter,
// and those need opposite outcomes — so the decision splits: every setter
// writes through (vendorFilterPersistAction), and the params-change effect
// ONLY restores (vendorFilterRestoreSearch). Pure — no react/router imports —
// so the decision logic unit-tests directly.

export const VENDORS_FILTERS_KEY = 'vendors.filters';

// The full set of list-state params the page round-trips through the URL.
// 'sort'/'dir' are list state like 'size' is: not filters (the page's
// `filtersActive` excludes both, so a sort alone must not claim "no vendors
// match your filters" or light up Clear filters), but they must round-trip, or
// the sidebar link would drop the admin's chosen column — and a sort-only URL
// would read as "no list state" and let the restore effect snap a stored filter
// back over it.
const FILTER_PARAM_KEYS = [
  'q',
  'status',
  'type',
  'region',
  'station',
  'page',
  'size',
  'sort',
  'dir',
] as const;

export function hasVendorFilterParams(params: URLSearchParams): boolean {
  return FILTER_PARAM_KEYS.some((k) => params.has(k));
}

/**
 * Stored value shape: `{ search: 'q=…&type=…' }` (URLSearchParams.toString()).
 * The string must carry at least one of the seven keys: a junk value like
 * 'utm_source=x' would otherwise restore, produce no filter key, never be
 * written back, and restore again on the next pass — render-looping the page
 * until localStorage is cleared by hand. localStorage is user-writable and
 * persists across deploys, so validate, don't trust. (The same check rejects
 * the empty string.)
 */
export function parseVendorsSearch(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const s = (raw as { search?: unknown }).search;
  if (typeof s !== 'string') return null;
  return hasVendorFilterParams(new URLSearchParams(s)) ? s : null;
}

export type VendorFilterPersistAction = { kind: 'write'; search: string } | { kind: 'clear' };

/**
 * Setter-side write-through: called by every list-state setter with the
 * params it is about to commit. Params still carrying a filter key are
 * mirrored to storage; params left with none CLEAR the key — emptying the
 * search box (or un-picking the last filter, or returning to page 1 / the
 * default page size) must empty storage too, or the restore effect would snap
 * the removed value straight back.
 */
export function vendorFilterPersistAction(params: URLSearchParams): VendorFilterPersistAction {
  return hasVendorFilterParams(params)
    ? { kind: 'write', search: params.toString() }
    : { kind: 'clear' };
}

/**
 * Restore-side decision for the params-change effect: the stored string, only
 * when the params carry no filter key AND storage holds one. Because setters
 * clear storage as they empty the params, that combination is reachable only
 * by a navigation that ran no setter — the sidebar "Vendors" link, a bare
 * bookmark, or the detail page's "← Vendors" link with an empty
 * vendorsListSearch. Converges: the restored URL carries a filter key, so the
 * next pass returns null.
 */
export function vendorFilterRestoreSearch(
  params: URLSearchParams,
  stored: string | null,
): string | null {
  if (hasVendorFilterParams(params)) return null;
  return stored;
}

/** Ordering contract for the Clear-filters control: storage first, URL second. */
export function clearVendorFilters(clearStorage: () => void, clearParams: () => void): void {
  clearStorage();
  clearParams();
}
